import type { ReactNode } from 'react'
import { sx } from '../lib/sx'

/** Logical screen size of an iPhone 15 / 15 Pro, in points. */
export const SCREEN_W = 393
export const SCREEN_H = 852
/** Metal side band, then the thin black bezel the display sits in. */
const BAND = 9
const BEZEL = 3
const INSET = BAND + BEZEL

export const FRAME_W = SCREEN_W + INSET * 2
export const FRAME_H = SCREEN_H + INSET * 2
/** Outer corner radius of the handset, so placeholders can match its silhouette. */
export const FRAME_RADIUS = 68

/**
 * Side-band finishes. The manager carries the Pro (titanium), the member a
 * standard 15 (aluminium) — a quiet way to tell the two handsets apart at a
 * glance when both are on screen.
 */
const FINISH = {
  titanium: {
    band: 'linear-gradient(145deg,#8e8e95 0%,#3d3d43 15%,#74747c 37%,#2f2f35 61%,#83838b 84%,#43434a 100%)',
    button: 'linear-gradient(180deg,#8a8a92,#3a3a40)',
  },
  aluminium: {
    band: 'linear-gradient(145deg,#c2ccd9 0%,#5c6672 15%,#a2adbb 37%,#4b5561 61%,#b3becc 84%,#5a6470 100%)',
    button: 'linear-gradient(180deg,#b2bdcb,#4d5763)',
  },
} as const

export type Finish = keyof typeof FINISH

export function PhoneShell({
  label,
  scale,
  finish = 'titanium',
  children,
}: {
  /** Debug/accessibility handle for the handset, e.g. "Manager phone". */
  label: string
  scale: number
  finish?: Finish
  children: ReactNode
}) {
  const skin = FINISH[finish]

  return (
    <div
      style={sx(
        `width:${Math.round(FRAME_W * scale)}px;height:${Math.round(FRAME_H * scale)}px;flex:none`
      )}
    >
      <div
        data-screen-label={label}
        style={sx(
          `position:relative;width:${FRAME_W}px;height:${FRAME_H}px;` +
            `transform:scale(${scale});transform-origin:top left;` +
            `border-radius:${FRAME_RADIUS}px;padding:${BAND}px;background:${skin.band};` +
            `box-shadow:0 30px 60px rgba(0,0,0,.55),0 1px 0 rgba(255,255,255,.28) inset,` +
            `0 -1px 0 rgba(0,0,0,.5) inset`
        )}
      >
        <SideButton skin={skin.button} side="left" top={168} height={34} />
        <SideButton skin={skin.button} side="left" top={228} height={64} />
        <SideButton skin={skin.button} side="left" top={306} height={64} />
        <SideButton skin={skin.button} side="right" top={256} height={104} />

        <div style={sx(`padding:${BEZEL}px;border-radius:60px;background:#08080a`)}>
          <div
            style={sx(
              `position:relative;width:${SCREEN_W}px;height:${SCREEN_H}px;border-radius:57px;` +
                `background:var(--bg-primary);overflow:hidden`
            )}
          >
            <StatusBar />
            {children}
            <DynamicIsland />
            <HomeIndicator />
          </div>
        </div>
      </div>
    </div>
  )
}

/** Volume, action and power keys, protruding slightly past the band. */
function SideButton({
  skin,
  side,
  top,
  height,
}: {
  skin: string
  side: 'left' | 'right'
  top: number
  height: number
}) {
  return (
    <span
      style={sx(
        `position:absolute;${side}:-3px;top:${top}px;width:4px;height:${height}px;` +
          `background:${skin};box-shadow:0 1px 2px rgba(0,0,0,.6);` +
          `border-radius:${side === 'left' ? '2px 0 0 2px' : '0 2px 2px 0'}`
      )}
    />
  )
}

/** The camera/Face ID cutout. Sits above screen content but below modals. */
function DynamicIsland() {
  return (
    <span
      style={sx(
        'position:absolute;top:11px;left:50%;transform:translateX(-50%);' +
          'width:125px;height:37px;border-radius:var(--radius-full);background:#000;z-index:8;' +
          'pointer-events:none'
      )}
    />
  )
}

function StatusBar() {
  return (
    <div
      style={sx(
        'position:absolute;top:0;left:0;right:0;height:56px;display:flex;align-items:center;' +
          'justify-content:space-between;padding:0 32px;z-index:6;pointer-events:none'
      )}
    >
      <span style={sx('font-size:var(--fs-sm);font-weight:var(--fw-bold);letter-spacing:var(--ls-sm)')}>
        9:41
      </span>
      <span style={sx('display:flex;gap:6px;align-items:flex-end')}>
        <SignalBars />
        <span
          style={sx(
            'width:16px;height:12px;background:var(--text-primary);' +
              '-webkit-mask:url(/assets/icons/wifi.svg) center/contain no-repeat;' +
              'mask:url(/assets/icons/wifi.svg) center/contain no-repeat'
          )}
        />
        <Battery />
      </span>
    </div>
  )
}

function SignalBars() {
  return (
    <span style={sx('display:flex;gap:2px;align-items:flex-end;height:11px')}>
      {[4, 6, 8, 11].map((h) => (
        <span
          key={h}
          style={sx(
            `width:3px;height:${h}px;border-radius:1px;background:var(--text-primary)`
          )}
        />
      ))}
    </span>
  )
}

function Battery() {
  return (
    <span style={sx('display:flex;align-items:center;gap:1px')}>
      <span
        style={sx(
          'width:22px;height:11px;border-radius:3px;padding:1.5px;' +
            'border:1px solid var(--text-primary);opacity:.9;display:flex'
        )}
      >
        <span style={sx('flex:1;border-radius:1.5px;background:var(--text-primary)')} />
      </span>
      <span
        style={sx(
          'width:1.5px;height:4px;border-radius:0 1px 1px 0;background:var(--text-primary);opacity:.5'
        )}
      />
    </span>
  )
}

function HomeIndicator() {
  return (
    <span
      style={sx(
        'position:absolute;bottom:9px;left:50%;transform:translateX(-50%);width:140px;height:5px;' +
          'border-radius:var(--radius-full);background:rgba(255,255,255,.4);z-index:7'
      )}
    />
  )
}

/** Caption + status dot above each handset on the stage. */
export function PhoneCaption({
  name,
  dotColor,
  muted,
}: {
  name: string
  dotColor: string
  muted?: boolean
}) {
  return (
    <div style={sx('display:flex;align-items:center;gap:8px;min-height:20px')}>
      <span
        style={sx(
          `width:8px;height:8px;flex:none;border-radius:var(--radius-full);background:${dotColor}`
        )}
      />
      <span
        style={sx(
          'font-size:var(--fs-sm);font-weight:var(--fw-medium);white-space:nowrap' +
            (muted ? ';color:var(--text-disabled)' : '')
        )}
      >
        {name}
      </span>
    </div>
  )
}
